'use client';

import { useState } from 'react';
import { SargassMap } from '@/components/SargassMap';
import { LangProvider } from '@/lib/i18n/LangContext';
import type { Lang } from '@/lib/i18n/types';
import type { BeachReport, Ile } from '@/lib/schema/bulletin';

/**
 * The interactive map embedded on a landing page. Reuses the exact same
 * `<SargassMap>` as the home app, but:
 *  - locked to a single island (no island switcher — the landing page is
 *    focused on one keyword),
 *  - wrapped in a `<LangProvider>` seeded with the PAGE's language so the
 *    map legend / offshore toggle render in the right language (the home
 *    app provides this via its own client context ; landing pages are
 *    server-rendered, so the map gets its own here),
 *  - marker clicks only highlight (the full beach detail + voting lives in
 *    the app, reachable via the CTA).
 */
export function LandingMap({
  ile,
  reports,
  afaiImageDate,
  lang,
  couleurLabels,
  initialSelectedSlug,
}: {
  ile: Ile;
  reports: ReadonlyArray<BeachReport>;
  afaiImageDate?: string;
  lang: Lang;
  /** Légende sur-mesure (widget embarquable, ton poli). Absent →
   * libellés du dictionnaire, comme sur les landings. */
  couleurLabels?: Record<'vert' | 'jaune' | 'orange' | 'rouge' | 'noir', string>;
  /** Page plage : marqueur surligné au chargement. */
  initialSelectedSlug?: string;
}) {
  const [selectedSlug, setSelectedSlug] = useState<string | null>(
    initialSelectedSlug ?? null,
  );
  return (
    <LangProvider value={lang}>
      <SargassMap
        ile={ile}
        reports={reports}
        onSelect={setSelectedSlug}
        selectedSlug={selectedSlug}
        afaiImageDate={afaiImageDate}
        couleurLabels={couleurLabels}
      />
    </LangProvider>
  );
}
